import React, { useEffect, useState } from "react"
import { MdHistory } from "react-icons/md"
import { toast } from "react-toastify"
import api from "../../../services/api"
import { formatCurrency, formatDataSize, formatDate } from "../../../utils/formatter"
import Modal from "../index"
import styles from "./modalAccountHistory.module.css"

const sectionLabels = {
    ACCOUNT: "Conta",
    COMPANY: "Empresa",
    CONTACTS: "Contatos",
    ADDRESS: "Endereço",
    PLAN: "Plano e cobrança",
    COMMERCIAL_TERMS: "Acordo comercial",
    PARTNER: "Parceria",
    MODULES: "Módulos e recursos",
    INTEGRATIONS: "Integrações",
    STATUS: "Status",
}

const pathLabels = {
    name: "Nome da conta",
    owner: "Responsável",
    "company.document": "CNPJ/CPF",
    "company.business_name": "Razão social",
    "company.fantasy_name": "Nome fantasia",
    "company.cityCode": "Código do município",
    "company.is_foreigner": "Empresa estrangeira",
    "address.zip_code": "CEP",
    "address.country": "País",
    "address.uf": "Estado",
    "address.city": "Cidade",
    "address.district": "Bairro",
    "address.road": "Logradouro",
    "address.number": "Número",
    "address.complement": "Complemento",
    "plan_details.plan_id": "Plano",
    "plan_details.discount": "Desconto legado",
    "plan_details.additional_discount": "Desconto adicional",
    "plan_details.next_invoice_discount": "Desconto da próxima fatura",
    "plan_details.additional.users": "Limite total de usuários",
    "plan_details.additional.leads": "Leads adicionais",
    "plan_details.additional.teams": "Equipes adicionais",
    "plan_details.expires_in": "Validade do plano",
    "plan_details.default_method": "Forma de pagamento",
    "plan_details.when_issue_invoice": "Emissão da Nota Fiscal",
    "plan_details.automatic_renovation": "Renovação automática",
    "commercial_terms.pricing_mode": "Modelo de preço",
    "commercial_terms.user_unit_price": "Valor por usuário",
    "commercial_terms.complimentary_users": "Usuários cortesia",
    "commercial_terms.complimentary_leads": "Leads cortesia",
    "commercial_terms.complimentary_storage_mb": "Armazenamento cortesia",
    "commercial_terms.reason": "Observações do acordo",
    "commercial_terms.effective_from": "Início da condição comercial",
    is_partner: "Conta parceira",
    partner_of: "Conta responsável",
    partner_permission: "Permissões do parceiro",
    payment_for: "Modelo de pagamento do parceiro",
    modules: "Módulos",
    "chat.credits": "Créditos do Chat",
    "chat.limit_inbox": "Limitar caixas de entrada",
    "chat.number_inbox": "Limite de caixas de entrada",
    "chat.limit_user": "Limitar usuários do Chat",
    "chat.number_user": "Limite de usuários do Chat",
    "chat.platforms": "Plataformas do Chat",
    "voip.credits": "Créditos de VoIP",
    "voip.cost_per_minute": "Custo por minuto",
    "voip.is_progressive_cost": "Custo progressivo",
    "voip.has_transcription": "Transcrição habilitada",
    "voip.keywords": "Palavras-chave da transcrição",
    "webhooks.raw_events_enabled": "Eventos brutos habilitados",
    "webhooks.retention_days": "Retenção dos eventos",
    "webhooks.auto_disable_after_failures": "Limite de falhas do webhook",
    "webhooks.alert_email": "E-mail de alerta do webhook",
    is_active: "Status operacional",
}

const valueLabels = {
    PLAN_DEFAULT: "Preço padrão do plano",
    PER_USER: "Preço negociado por usuário",
    AFTER_PAYMENT: "Após o pagamento",
    ON_CHARGE: "Na emissão da cobrança",
    BANK_SLIP: "Boleto bancário",
    CREDIT_CARD: "Cartão de crédito",
    DEBIT_CARD: "Cartão de débito",
    PIX: "Pix",
    NOT_ACCESS: "Sem acesso",
    READ_ONLY: "Somente leitura",
    ONLY_ESSENTIAL: "Somente alterações essenciais",
    FULL_ACCESS: "Acesso total",
    ACCOUNT: "Por conta ativa",
    USER: "Por usuário ativo",
    whatsapp: "WhatsApp Lite",
    whatsapp_api: "WhatsApp API",
    direct: "Instagram Direct",
    messenger: "Messenger",
}

const currencyPaths = new Set([
    "plan_details.discount",
    "plan_details.additional_discount",
    "plan_details.next_invoice_discount",
    "commercial_terms.user_unit_price",
    "voip.cost_per_minute",
])

const getDetailLabel = (path = "") => {
    if (pathLabels[path]) return pathLabels[path]

    const additionalChargeMatch = path.match(/^commercial_terms\.additional_charges\.(\d+)\.(description|amount)$/)
    if (additionalChargeMatch) {
        return `Cobrança ${Number(additionalChargeMatch[1]) + 1} · ${additionalChargeMatch[2] === "description" ? "Descrição" : "Valor mensal"}`
    }

    const contactMatch = path.match(/^contacts\.(\d+)\.(name|phone|email|department)$/)
    if (contactMatch) {
        const labels = { name: "Nome", phone: "Celular", email: "E-mail", department: "Departamento" }
        return `Contato ${Number(contactMatch[1]) + 1} · ${labels[contactMatch[2]]}`
    }

    const tierMatch = path.match(/^payment_by_user\.(\d+)\.(start_amount|end_amount|value)$/)
    if (tierMatch) {
        const labels = { start_amount: "Quantidade inicial", end_amount: "Quantidade final", value: "Valor" }
        return `Faixa ${Number(tierMatch[1]) + 1} · ${labels[tierMatch[2]]}`
    }

    return path.split(".").filter(part => !/^\d+$/.test(part)).join(" › ") || "Alteração"
}

const formatDetailValue = (detail, side) => {
    const label = detail?.[`${side}_label`]
    if (label) return label

    const value = detail?.[side]
    if ((value === null || value === undefined || value === "") && detail.path === "plan_details.automatic_renovation") return "Ativada"
    if ((value === null || value === undefined || value === "") && detail.path === "commercial_terms.pricing_mode") return "Preço padrão do plano"
    if ((value === null || value === undefined || value === "") && detail.path === "plan_details.when_issue_invoice") return "Após o pagamento"
    if (value === null || value === undefined || value === "") return "Não informado"
    if (typeof value === "boolean") return value ? "Sim" : "Não"
    if (currencyPaths.has(detail.path) || /^payment_by_user\.\d+\.value$/.test(detail.path) || /^commercial_terms\.additional_charges\.\d+\.amount$/.test(detail.path)) return formatCurrency(value)
    if (detail.path === "commercial_terms.complimentary_storage_mb") return formatDataSize(value)
    if (detail.path.endsWith("expires_in") || detail.path.endsWith("effective_from")) return formatDate(value, "DD/MM/YYYY HH:mm")
    if (Array.isArray(value)) return value.length ? value.map(item => valueLabels[item] || item).join(", ") : "Nenhum"
    if (typeof value === "object") return JSON.stringify(value)
    return valueLabels[value] || value.toString()
}

const actionLabels = {
    CREATED: "Criado por",
    UPDATED: "Alterado por",
    STATUS_CHANGED: "Status alterado por",
    TRIAL_EXTENDED: "Período alterado por",
}

const ModalAccountHistory = ({ accountId, accountName, closeModal }) => {
    const [events, setEvents] = useState([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        api.get(`/accounts/${accountId}/change-history`, { params: { limit: 100 } })
            .then(response => setEvents(response?.data?.data || []))
            .catch(err => toast.error(err?.response?.data?.message || "Não foi possível carregar o histórico."))
            .finally(() => setLoading(false))
    }, [accountId])

    return (
        <Modal icon={<MdHistory />} title="Histórico de alterações" description={accountName || "Conta"} closeModal={closeModal} fullWidith={true}>
            <div className={styles.timeline}>
                {loading && <span className={styles.empty}>Carregando histórico...</span>}
                {!loading && events.length === 0 && <span className={styles.empty}>Nenhuma alteração auditada ainda.</span>}
                {events.map(event => (
                    <article className={styles.event} key={event._id}>
                        <div className={styles.marker} />
                        <div className={styles.card}>
                            <div className={styles.header}>
                                <strong>{sectionLabels[event.section] || event.section}</strong>
                                <time>{formatDate(event.created_at, "DD/MM/YYYY HH:mm")}</time>
                            </div>
                            <span className={styles.actor}>{actionLabels[event.action] || "Alterado por"} {event.actor_name || "Sistema"}</span>
                            {event.reason && <p className={styles.reason}>Motivo: {event.reason}</p>}
                            <div className={styles.changes}>
                                {(event.details || []).map((detail, index) => (
                                    <div className={styles.change} key={`${event._id}-${detail.path}-${index}`}>
                                        <strong>{getDetailLabel(detail.path)}</strong>
                                        <div className={styles.comparison}>
                                            <div>
                                                <small>Antes</small>
                                                <span>{formatDetailValue(detail, "before")}</span>
                                            </div>
                                            <span className={styles.arrow} aria-hidden="true">→</span>
                                            <div>
                                                <small>Depois</small>
                                                <span>{formatDetailValue(detail, "after")}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {(!event.details || event.details.length === 0) &&
                                    <span className={styles.noDetails}>Nenhuma mudança funcional encontrada neste registro.</span>
                                }
                            </div>
                        </div>
                    </article>
                ))}
            </div>
        </Modal>
    )
}

export default ModalAccountHistory
